import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, catchError, map, of, switchMap, tap, throwError } from 'rxjs';
import { Security, SecurityPricePoint } from './security.model';
import { showErrorDialog } from '../../shared/utils/utils';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { LocalStorageService } from 'ngx-localstorage';
import { differenceInDays } from 'date-fns';
import { STORE_SECURITIES } from '../../shared/types/constants';

@Injectable({
  providedIn: 'root'
})
export class SecuritiesService {
  private http = inject(HttpClient);
  private logger = inject(NGXLogger);
  private dbService = inject(NgxIndexedDBService);
  private storage = inject(LocalStorageService);

  private apiEndpoint = `${environment.apiEndpoint}/api/securities`;
  public securitiesSet: string | null | undefined = null;
  public securitiesSet$ = new BehaviorSubject<boolean>(false);

  public listSecurities$(): Observable<Security[]> {
    return this.dbService.getAll<Security>(STORE_SECURITIES).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public listSecuritiesExchanges$(): Observable<string[]> {
    return this.dbService.getAll<Security>(STORE_SECURITIES).pipe(
      map((securities) => {
        const exchanges = securities.map((s) => s.exchange).filter((v, i, a) => a.indexOf(v) === i);
        return exchanges.sort();
      }),
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public listSecurityAssetClasses$(): Observable<string[]> {
    return this.dbService.getAll<Security>(STORE_SECURITIES).pipe(
      map((securities) => {
        const assetClasses = securities.map((s) => s.type).filter((v, i, a) => v && a.indexOf(v) === i) as string[];
        return assetClasses.sort();
      }),
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public searchExchanges$(query: string): Observable<string[]> {
    const q = query.toLowerCase().trim();
    return this.dbService.getAll<Security>(STORE_SECURITIES).pipe(
      switchMap((securities) =>
        securities.length > 0
          ? of(securities)
          : this.loadSecuritiesIntoCache$(false).pipe(
              switchMap(() => this.dbService.getAll<Security>(STORE_SECURITIES)),
              catchError(() => of([] as Security[]))
            )
      ),
      map((securities) => {
        const exchanges = [...new Set(securities.map((s) => s.exchange).filter(Boolean))].sort() as string[];
        return q ? exchanges.filter((e) => e.toLowerCase().includes(q)) : exchanges;
      }),
      catchError((error) => {
        this.logger.error(error);
        return of([] as string[]);
      })
    );
  }

  public searchSecurities$(query: string): Observable<Security[]> {
    const q = query.toLowerCase().trim();
    return this.dbService.getAll<Security>(STORE_SECURITIES).pipe(
      map((securities) =>
        securities
          .filter(
            (s) =>
              s.name?.toLowerCase().includes(q) ||
              s.faSecurityCode?.toLowerCase().includes(q) ||
              s.isin?.toLowerCase().includes(q) ||
              s.tradeCode?.toLowerCase().includes(q)
          )
          .slice(0, 100)
      ),
      catchError((error) => {
        this.logger.error(error);
        return of([] as Security[]);
      })
    );
  }

  public getByCode$(faSecurityCode: string): Observable<Security | undefined> {
    return this.dbService.getByIndex<Security>(STORE_SECURITIES, 'faSecurityCode', faSecurityCode).pipe(
      catchError((error) => {
        this.logger.error(error);
        return of(undefined);
      })
    );
  }

  public getLastClose$(faSecurityCode: string): Observable<number> {
    return this.http
      .get(`${this.apiEndpoint}/last-close/${faSecurityCode}`, {
        responseType: 'text'
      })
      .pipe(
        map((res) => Number.parseFloat(res)),
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  /**
   * Daily closes over the last `days` days. Deliberately not wrapped in an error dialog — the chart is
   * context beside the order, so a missing history should not interrupt entering one.
   */
  public getPriceHistory$(faSecurityCode: string, days = 30): Observable<SecurityPricePoint[]> {
    return this.http
      .get<SecurityPricePoint[]>(`${this.apiEndpoint}/${encodeURIComponent(faSecurityCode)}/price-history`, {
        params: { days: days.toString() }
      })
      .pipe(
        catchError((error) => {
          this.logger.error(error);

          return of([]);
        })
      );
  }

  public getOneByIsin$(isin: string): Observable<Security> {
    return this.http.get<Security>(`${this.apiEndpoint}/isin/${isin}`).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public refreshSecuritiesCache$(): Observable<Security[]> {
    return this.storeSecurities$(this.http.post<Security[]>(`${this.apiEndpoint}/refresh`, null));
  }

  public checkAndLoadSecuritiesIntoCache(): Observable<boolean> {
    const securitiesSet = this.storage.get<string>('securities_set');

    if (!securitiesSet || differenceInDays(new Date(), new Date(securitiesSet)) > 1) {
      return this.loadSecuritiesIntoCache$(false).pipe(map(() => true));
    }
    this.securitiesSet = securitiesSet;
    this.logger.log('Successfully loaded securities from cache');
    this.securitiesSet$.next(true);
    return of(true);
  }

  private loadSecuritiesIntoCache$(showDialog = true): Observable<Security[]> {
    return this.storeSecurities$(this.http.get<Security[]>(this.apiEndpoint), showDialog);
  }

  private storeSecurities$(request$: Observable<Security[]>, showDialog = true): Observable<Security[]> {
    return request$.pipe(
      catchError((error) => {
        this.logger.error(error);
        if (showDialog) showErrorDialog(error);

        return throwError(() => error);
      }),
      switchMap((securities) => {
        return this.dbService.clear(STORE_SECURITIES).pipe(
          tap(() => this.logger.log('Successfully cleared securities DB')),
          switchMap(() => {
            return this.dbService.bulkAdd(STORE_SECURITIES, securities).pipe(
              tap(() => {
                this.logger.log(`Successfully loaded ${securities.length} securities to DB`);
                const securitiesSet = new Date().toISOString();
                this.storage.set('securities_set', securitiesSet);
                this.securitiesSet = securitiesSet;
                this.securitiesSet$.next(true);
              }),
              map(() => securities)
            );
          })
        );
      })
    );
  }
}
